import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import CustomDateRangePicker from '../../../../components/CustomDateRangePicker';
import { PieChartCard } from './style';
import { AppDispatch } from '../../../../store';
import { fetchFacilityShiftStatusChart, selectFacilityShiftStatusChart } from '../../../../modules/facility';
import StatusIcon from '../../../../assets/FacilityDashboard/shift-status.svg'

const ShiftStatus: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([dayjs(), dayjs()]);
  const shiftStatusData = useSelector(selectFacilityShiftStatusChart);
  const [hoveredSlice, setHoveredSlice] = useState<number | null>(null);

  useEffect(() => {
    const startDate = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined;
    const endDate = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined;

    if (startDate && endDate) {
      dispatch(fetchFacilityShiftStatusChart({ startDate, endDate }));
    }
  }, [dispatch, dateRange]);

  // Calculate percentages for the pie chart
  const total = shiftStatusData?.total_workers || 0;
  const present = shiftStatusData?.workers_present || 0;
  const late = shiftStatusData?.late_absent || 0;

  // Use the larger of 'total' or 'present + late' to ensure the chart doesn't overflow
  // This handles cases where the API returns inconsistent data (e.g., more present than total)
  const chartBase = Math.max(total, present + late);

  const presentPercentage = chartBase > 0 ? (present / chartBase) * 100 : 0;
  const latePercentage = chartBase > 0 ? (late / chartBase) * 100 : 0;


  return (
    <PieChartCard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '4px'
          }}>
            <img src={StatusIcon} alt="shift-status-icon" width="48" height="48" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Shift Status</Typography>
            <Typography variant="body2" color="textSecondary">{shiftStatusData?.last_updated || 'Last updated...'}</Typography>
          </Box>
        </Box>
        <Box sx={{ width: 210 }}>
          <CustomDateRangePicker
            value={dateRange}
            onChange={(newValue) => setDateRange(newValue)}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', flex: 1 }}>
        {/* SVG Pie Chart */}
        <Box sx={{
          width: 170,
          height: 170,
          position: 'relative'
        }}>
          <svg viewBox="-1 -1 22 22" width="100%" height="100%" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
            {/* 
                Helper to calculate path data. 
                Since we can't define functions inside the JSX return, we'll implement inline or logic here.
                Actually, simpler to use a cumulative approach. 
             */}
            {(() => {
              const getCoordinatesForPercent = (percent: number) => {
                const x = Math.cos(2 * Math.PI * percent);
                const y = Math.sin(2 * Math.PI * percent);
                return [x, y];
              };

              const slices = [
                { percent: presentPercentage / 100, color: 'rgba(93, 143, 118, 0.65)' }, // Present (Green)
                { percent: latePercentage / 100, color: 'rgba(255, 60, 0, 0.65)' },    // Late (Red)
                { percent: (100 - presentPercentage - latePercentage) / 100, color: 'rgba(224, 224, 224, 0.65)' } // Remaining (Grey)
              ];

              let cumulativePercent = 0;

              return slices.map((slice, index) => {
                // Handle 100% or 0% cases to avoid rendering artifacts
                if (slice.percent === 1) {
                  return (
                    <circle cx="10" cy="10" r="10" fill={slice.color} stroke="white" strokeWidth="0.8" key={index} />
                  );
                }
                if (slice.percent === 0) return null;

                const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
                cumulativePercent += slice.percent;
                const [endX, endY] = getCoordinatesForPercent(cumulativePercent);

                const largeArcFlag = slice.percent > 0.5 ? 1 : 0;

                // Coordinate space: center is 10,10, radius 10
                const startXPos = 10 + 10 * startX;
                const startYPos = 10 + 10 * startY;
                const endXPos = 10 + 10 * endX;
                const endYPos = 10 + 10 * endY;

                const pathData = [
                  `M 10 10`,
                  `L ${startXPos} ${startYPos}`,
                  `A 10 10 0 ${largeArcFlag} 1 ${endXPos} ${endYPos}`,
                  `L 10 10`,
                ].join(' ');

                const isHovered = hoveredSlice === index;

                return (
                  <path
                    key={index}
                    d={pathData}
                    fill={slice.color}
                    stroke="white"
                    strokeWidth="0.8"
                    onMouseEnter={() => setHoveredSlice(index)}
                    onMouseLeave={() => setHoveredSlice(null)}
                    style={{
                      transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                      transformOrigin: '10px 10px',
                      transition: 'transform 0.3s ease',
                      cursor: 'pointer',
                      filter: isHovered ? 'drop-shadow(0px 4px 6px rgba(0,0,0,0.2))' : 'none',
                      zIndex: isHovered ? 10 : 1 // SVG z-index doesn't work like HTML, but filter helps visual layering
                    }}
                  />
                );
              });
            })()}
            {/* Outer Border */}
            <circle cx="10" cy="10" r="10" fill="none" stroke="#000000" strokeWidth="0.1" opacity="0.2" style={{ pointerEvents: 'none' }} />
          </svg>
        </Box>

        <Box sx={{ width: '150px' }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 5, height: 32, bgcolor: 'rgba(93, 110, 100, 0.8)', borderRadius: '4px' }} />
            <Box>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.2 }}>Total Workers</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{chartBase}</Typography>
            </Box>
          </Box>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 5, height: 32, bgcolor: 'rgba(93, 143, 118, 0.4)', borderRadius: '4px' }} />
            <Box>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.2 }}>Workers Present</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{present}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 5, height: 32, bgcolor: 'rgba(255, 60, 0, 0.8)', borderRadius: '4px' }} />
            <Box>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.2 }}>Late/Absent</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{late}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </PieChartCard>
  );
};

export default ShiftStatus;
